import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { API_LINK } from '../../apiLink/apiLink';

const StudentAssignment = ({ onNavigate, userInfo }) => {
    // Layout State
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        submitted: 0,
        graded: 0,
        overdue: 0
    });
    const [filter, setFilter] = useState('all');
    const [selectedSubject, setSelectedSubject] = useState('all');
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [submissionText, setSubmissionText] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);

    // Student Data state removed (handled by layout)

    // Check if mobile on mount and resize
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Fetch assignments on component mount
    useEffect(() => {
        fetchAssignments();
    }, []);

    const fetchAssignments = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem('token') || sessionStorage.getItem('token');

            if (!token) {
                setError('Not authenticated. Please login again.');
                setLoading(false);
                return;
            }

            // Fetch assignments
            const response = await fetch(`${API_LINK}/api/student/assignments`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    window.location.href = '/';
                    return;
                }
                setError('Failed to load assignments. Please try again.');
                setLoading(false);
                return;
            }

            const result = await response.json();

            if (result.success && result.data) {
                setAssignments(result.data.assignments || []);
                if (result.data.stats) {
                    setStats(result.data.stats);
                } else {
                    // Calculate stats from assignments
                    const asgns = result.data.assignments || [];
                    setStats({
                        total: asgns.length,
                        pending: asgns.filter(a => !a.isSubmitted && !a.isOverdue).length,
                        submitted: asgns.filter(a => a.isSubmitted && a.status === 'submitted').length,
                        graded: asgns.filter(a => a.status === 'graded').length,
                        overdue: asgns.filter(a => a.isOverdue && !a.isSubmitted).length
                    });
                }
            } else {
                setAssignments([]);
                setStats({ total: 0, pending: 0, submitted: 0, graded: 0, overdue: 0 });
            }

        } catch (error) {
            console.error('Error fetching assignments:', error);
            setError('Failed to load assignments. Please check your connection.');
        } finally {
            setLoading(false);
        }
    };


    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);

        // Validate file size (max 10MB per file)
        const maxSize = 10 * 1024 * 1024;
        const invalidFiles = files.filter(file => file.size > maxSize);

        if (invalidFiles.length > 0) {
            alert('Some files exceed the 10MB size limit. Please select smaller files.');
            return;
        }

        // Limit to 3 files
        if (files.length > 3) {
            alert('Maximum 3 files allowed');
            return;
        }

        setSelectedFiles(files);
    };

    const handleSubmitAssignment = async () => {
        if (!submissionText && selectedFiles.length === 0) {
            alert('Please provide either text submission or upload file(s)');
            return;
        }

        try {
            setSubmitting(true);

            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const formData = new FormData();

            formData.append('submissionText', submissionText);

            selectedFiles.forEach((file) => {
                formData.append('files', file);
            });

            const response = await fetch(
                `${API_LINK}/api/student/assignments/${selectedAssignment.assignmentId}/submit`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: formData
                }
            );

            const result = await response.json();

            if (result.success) {
                alert(result.message);
                setShowSubmitModal(false);
                setSubmissionText('');
                setSelectedFiles([]);
                setSelectedAssignment(null);
                fetchAssignments(); // Refresh assignments
            } else {
                alert(result.message || 'Failed to submit assignment');
            }

        } catch (error) {
            console.error('Error submitting assignment:', error);
            alert('Failed to submit assignment. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            'pending': '#ff9800',
            'submitted': '#2196f3',
            'graded': '#4caf50',
            'late': '#f44336',
            'overdue': '#d32f2f'
        };
        return colors[status] || '#666';
    };

    const getStatusBadge = (assignment) => {
        let status = assignment.status;
        let text = status.charAt(0).toUpperCase() + status.slice(1);

        if (assignment.isOverdue && !assignment.isSubmitted) {
            status = 'overdue';
            text = 'Overdue';
        }

        return (
            <span style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#ffffff',
                backgroundColor: getStatusColor(status),
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
            }}>
                {text}
            </span>
        );
    };

    const getUniqueSubjects = () => {
        const subjects = [...new Set(assignments.map(a => a.courseName))];
        return subjects.sort();
    };

    const filteredAssignments = assignments.filter(assignment => {
        // Filter by status
        let statusMatch = true;
        if (filter === 'pending') statusMatch = !assignment.isSubmitted && !assignment.isOverdue;
        else if (filter === 'submitted') statusMatch = assignment.isSubmitted && assignment.status === 'submitted';
        else if (filter === 'graded') statusMatch = assignment.status === 'graded';
        else if (filter === 'overdue') statusMatch = assignment.isOverdue;

        // Filter by subject
        const subjectMatch = selectedSubject === 'all' || assignment.courseName === selectedSubject;

        return statusMatch && subjectMatch;
    });

    // Get user initials


    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#f4f7fa',
                flexDirection: 'column',
                gap: '1rem'
            }}>
                <div style={{
                    width: '50px',
                    height: '50px',
                    border: '4px solid #e3f2fd',
                    borderTop: '4px solid #238548',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Assignments...</p>
                <style>
                    {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
                </style>
            </div>
        );
    }

    return (
        <Layout role="student"
            onNavigate={onNavigate}
            activePage="student-assignments"
            pageTitle="Assignments"
        >
            {/* Error Display */}
            {error && (
                <div style={{
                    background: '#ffebee',
                    border: '1px solid #ef5350',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1.5rem',
                    color: '#c62828'
                }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            {/* Statistics Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: isMobile ? '0.75rem' : '1rem',
                marginBottom: isMobile ? '1rem' : '2rem'
            }}>
                <div style={{
                    background: '#ffffff',
                    padding: isMobile ? '0.8rem 0.5rem' : '1.5rem',
                    borderRadius: isMobile ? '10px' : '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    textAlign: 'center'
                }}>
                    <div style={{ fontSize: isMobile ? '1.5rem' : '2rem', color: '#238548', marginBottom: isMobile ? '0.3rem' : '0.5rem' }}>📚</div>
                    <div style={{ fontSize: isMobile ? '1.3rem' : '2rem', fontWeight: 'bold', color: '#238548' }}>{stats.total}</div>
                    <div style={{ color: '#666', fontSize: isMobile ? '0.65rem' : '0.9rem' }}>Total Assignments</div>
                </div>

                <div style={{
                    background: '#ffffff',
                    padding: isMobile ? '0.8rem 0.5rem' : '1.5rem',
                    borderRadius: isMobile ? '10px' : '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    textAlign: 'center'
                }}>
                    <div style={{ fontSize: isMobile ? '1.5rem' : '2rem', color: '#ff9800', marginBottom: isMobile ? '0.3rem' : '0.5rem' }}>⏳</div>
                    <div style={{ fontSize: isMobile ? '1.3rem' : '2rem', fontWeight: 'bold', color: '#ff9800' }}>{stats.pending}</div>
                    <div style={{ color: '#666', fontSize: isMobile ? '0.65rem' : '0.9rem' }}>Pending</div>
                </div>

                <div style={{
                    background: '#ffffff',
                    padding: isMobile ? '0.8rem 0.5rem' : '1.5rem',
                    borderRadius: isMobile ? '10px' : '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    textAlign: 'center'
                }}>
                    <div style={{ fontSize: isMobile ? '1.5rem' : '2rem', color: '#2196f3', marginBottom: isMobile ? '0.3rem' : '0.5rem' }}>✅</div>
                    <div style={{ fontSize: isMobile ? '1.3rem' : '2rem', fontWeight: 'bold', color: '#2196f3' }}>{stats.submitted}</div>
                    <div style={{ color: '#666', fontSize: isMobile ? '0.65rem' : '0.9rem' }}>Submitted</div>
                </div>

                <div style={{
                    background: '#ffffff',
                    padding: isMobile ? '0.8rem 0.5rem' : '1.5rem',
                    borderRadius: isMobile ? '10px' : '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    textAlign: 'center'
                }}>
                    <div style={{ fontSize: isMobile ? '1.5rem' : '2rem', color: '#4caf50', marginBottom: isMobile ? '0.3rem' : '0.5rem' }}>🏆</div>
                    <div style={{ fontSize: isMobile ? '1.3rem' : '2rem', fontWeight: 'bold', color: '#4caf50' }}>{stats.graded}</div>
                    <div style={{ color: '#666', fontSize: isMobile ? '0.65rem' : '0.9rem' }}>Graded</div>
                </div>

                <div style={{
                    background: '#ffffff',
                    padding: isMobile ? '0.8rem 0.5rem' : '1.5rem',
                    borderRadius: isMobile ? '10px' : '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    textAlign: 'center'
                }}>
                    <div style={{ fontSize: isMobile ? '1.5rem' : '2rem', color: '#f44336', marginBottom: isMobile ? '0.3rem' : '0.5rem' }}>⚠️</div>
                    <div style={{ fontSize: isMobile ? '1.3rem' : '2rem', fontWeight: 'bold', color: '#f44336' }}>{stats.overdue}</div>
                    <div style={{ color: '#666', fontSize: isMobile ? '0.65rem' : '0.9rem' }}>Overdue</div>
                </div>
            </div>

            {/* Filters Section */}
            <div style={{
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                padding: '1.5rem',
                marginBottom: '1.5rem'
            }}>


                {/* Status Filter Tabs */}
                <div>
                    <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        color: '#333',
                        fontWeight: '600',
                        fontSize: '0.95rem'
                    }}>
                        📊 Filter by Status
                    </label>
                    <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        flexWrap: 'wrap'
                    }}>
                        {[
                            { key: 'all', label: 'All', count: stats.total },
                            { key: 'pending', label: 'Pending', count: stats.pending },
                            { key: 'submitted', label: 'Submitted', count: stats.submitted },
                            { key: 'graded', label: 'Graded', count: stats.graded },
                            { key: 'overdue', label: 'Overdue', count: stats.overdue }
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setFilter(tab.key)}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: filter === tab.key ? '#238548' : '#f5f5f5',
                                    color: filter === tab.key ? '#ffffff' : '#333',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: '500',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    if (filter !== tab.key) {
                                        e.target.style.backgroundColor = '#e0e0e0';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (filter !== tab.key) {
                                        e.target.style.backgroundColor = '#f5f5f5';
                                    }
                                }}
                            >
                                {tab.label} ({tab.count})
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main Content Layout - Filter Left, Assignments Right */}
            <div style={{
                display: 'flex',
                gap: '1.5rem',
                flexDirection: 'column',
                // alignItems: 'flex-start'
            }}>
                {/* Left Side - Subject Filter */}
                <div style={{
                    width: '280px',
                    flexShrink: 0,
                    position: 'sticky',
                    top: isMobile ? '80px' : '180px'
                }}>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        padding: '1.5rem'
                    }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.75rem',
                            color: '#333',
                            fontWeight: '600',
                            fontSize: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}>
                            <span style={{ fontSize: '1.2rem' }}>📚</span>
                            Filter by Subject
                        </label>
                        <select
                            value={selectedSubject}
                            onChange={(e) => setSelectedSubject(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.75rem 1rem',
                                border: '2px solid #e0e0e0',
                                borderRadius: '8px',
                                fontSize: '0.95rem',
                                color: '#333',
                                backgroundColor: '#ffffff',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                outline: 'none'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#238548'}
                            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                        >
                            <option value="all">All Subjects ({assignments.length})</option>
                            {getUniqueSubjects().map(subject => {
                                const count = assignments.filter(a => a.courseName === subject).length;
                                return (
                                    <option key={subject} value={subject}>
                                        {subject} ({count})
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                </div>

                {/* Right Side - Assignments List */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {filteredAssignments.length === 0 ? (
                        <div style={{
                            background: '#ffffff',
                            padding: '3rem',
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            textAlign: 'center'
                        }}>
                            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
                            <h3 style={{ color: '#666', margin: 0 }}>No assignments found</h3>
                            <p style={{ color: '#999', margin: '0.5rem 0 0 0' }}>
                                {filter === 'all' ? 'No assignments available yet' : `No ${filter} assignments`}
                            </p>
                        </div>
                    ) : (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                            gap: '1.5rem'
                        }}>
                            {filteredAssignments.map((assignment) => (
                                <div
                                    key={assignment.assignmentId}
                                    style={{
                                        background: '#ffffff',
                                        borderRadius: '12px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        overflow: 'hidden',
                                        transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                                    }}
                                >
                                    {/* Assignment Header */}
                                    <div style={{
                                        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                                        color: '#ffffff',
                                        padding: '1.5rem',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start'
                                    }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div style={{ margin: '0 0 0.5rem 0', fontSize: '1.3rem' }}>
                                                    {assignment.courseName}
                                                </div>

                                                <div>
                                                    {getStatusBadge(assignment)}
                                                </div>
                                            </div>


                                            <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>
                                                📚 {assignment.title}
                                            </div>
                                            <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>
                                                • 📅 Due: {formatDate(assignment.dueDate)}
                                            </div>
                                        </div>


                                    </div>

                                    {/* Assignment Body */}
                                    <div style={{ padding: '1.5rem' }}>
                                        <p style={{
                                            color: '#666',
                                            lineHeight: '1.6',
                                            marginBottom: '1rem',
                                            textAlign: 'left'
                                        }}>
                                            {assignment.description}
                                        </p>

                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                            gap: '1rem',
                                            marginBottom: '1rem'
                                        }}>
                                            <div>
                                                <strong style={{ color: '#333' }}>Total Marks:</strong>
                                                <span style={{ marginLeft: '0.5rem', color: '#666' }}>
                                                    {assignment.totalMarks}
                                                </span>
                                            </div>
                                            <div>
                                                <strong style={{ color: '#333' }}>Semester:</strong>
                                                <span style={{ marginLeft: '0.5rem', color: '#666' }}>
                                                    {assignment.semester}
                                                </span>
                                            </div>
                                            {assignment.isSubmitted && (
                                                <div>
                                                    <strong style={{ color: '#333' }}>Submitted:</strong>
                                                    <span style={{ marginLeft: '0.5rem', color: '#666' }}>
                                                        {formatDateTime(assignment.submittedAt)}
                                                    </span>
                                                </div>
                                            )}
                                            {assignment.marksObtained !== null && (
                                                <div>
                                                    <strong style={{ color: '#333' }}>Marks Obtained:</strong>
                                                    <span style={{
                                                        marginLeft: '0.5rem',
                                                        color: '#238548',
                                                        fontWeight: 'bold',
                                                        fontSize: '1.1rem'
                                                    }}>
                                                        {assignment.marksObtained}/{assignment.totalMarks}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {assignment.feedback && (
                                            <div style={{
                                                background: '#f0f8f4',
                                                border: '1px solid #97d6ac',
                                                borderRadius: '8px',
                                                padding: '1rem',
                                                marginBottom: '1rem'
                                            }}>
                                                <strong style={{ color: '#238548', display: 'block', marginBottom: '0.5rem' }}>
                                                    📝 Teacher's Feedback:
                                                </strong>
                                                <p style={{ margin: 0, color: '#666', lineHeight: '1.6' }}>
                                                    {assignment.feedback}
                                                </p>
                                            </div>
                                        )}


                                        {/* Action Buttons */}
                                        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                                            {/* View Assignment Button */}
                                            <button
                                                onClick={() => {
                                                    setSelectedAssignment(assignment);
                                                    setShowViewModal(true);
                                                }}
                                                style={{
                                                    flex: 1,
                                                    padding: '0.75rem 1rem',
                                                    backgroundColor: '#ffffff',
                                                    color: '#238548',
                                                    border: '2px solid #238548',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    fontSize: '0.95rem',
                                                    fontWeight: '600',
                                                    transition: 'all 0.3s ease',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '0.5rem'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#f0f8f4';
                                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = '#ffffff';
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                }}
                                            >
                                                <span style={{ fontSize: '1.1rem' }}>👁️</span>
                                                View Assignment
                                            </button>

                                            {/* Submit Assignment Button */}
                                            <button
                                                onClick={() => {
                                                    setSelectedAssignment(assignment);
                                                    setShowSubmitModal(true);
                                                }}
                                                disabled={assignment.isSubmitted || assignment.isOverdue}
                                                style={{
                                                    flex: 1,
                                                    padding: '0.75rem 1rem',
                                                    backgroundColor: (assignment.isSubmitted || assignment.isOverdue) ? '#ccc' : '#238548',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    cursor: (assignment.isSubmitted || assignment.isOverdue) ? 'not-allowed' : 'pointer',
                                                    fontSize: '0.95rem',
                                                    fontWeight: '600',
                                                    transition: 'all 0.3s ease',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '0.5rem',
                                                    opacity: (assignment.isSubmitted || assignment.isOverdue) ? 0.6 : 1
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!assignment.isSubmitted && !assignment.isOverdue) {
                                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!assignment.isSubmitted && !assignment.isOverdue) {
                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                        e.currentTarget.style.boxShadow = 'none';
                                                    }
                                                }}
                                            >
                                                <span style={{ fontSize: '1.1rem' }}>
                                                    {assignment.isSubmitted ? '✅' : (assignment.isOverdue ? '🚫' : '📤')}
                                                </span>
                                                {assignment.isSubmitted ? 'Submitted' : (assignment.isOverdue ? 'Overdue' : 'Submit Assignment')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>


            {/* View Assignment Modal */}
            {showViewModal && selectedAssignment && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000,
                    padding: '1rem'
                }}>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '12px',
                        maxWidth: '650px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                    }}>
                        {/* Modal Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                            color: '#ffffff',
                            padding: '1.5rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div>
                                <h3 style={{ margin: '0 0 0.3rem 0', fontSize: '1.2rem' }}>📋 Assignment Details</h3>
                                <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>{selectedAssignment.courseName}</div>
                            </div>
                            <button
                                onClick={() => { setShowViewModal(false); setSelectedAssignment(null); }}
                                style={{
                                    background: 'rgba(255,255,255,0.2)',
                                    border: 'none',
                                    color: '#ffffff',
                                    fontSize: '1.3rem',
                                    cursor: 'pointer',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: '1.5rem' }}>
                            {/* Status Badge */}
                            <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                {getStatusBadge(selectedAssignment)}
                                {selectedAssignment.courseCode && (
                                    <span style={{ background: '#e3f2fd', color: '#1565c0', padding: '0.2rem 0.6rem', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600 }}>
                                        {selectedAssignment.courseCode}
                                    </span>
                                )}
                            </div>

                            {/* Title */}
                            <h2 style={{ margin: '0 0 1rem', fontSize: '1.3rem', color: '#1a1a2e', fontWeight: 700 }}>
                                {selectedAssignment.title}
                            </h2>

                            {/* Description */}
                            <div style={{
                                background: '#f8f9fa',
                                borderRadius: '8px',
                                padding: '1rem',
                                marginBottom: '1.5rem',
                                borderLeft: '4px solid #238548'
                            }}>
                                <strong style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontSize: '0.9rem' }}>📝 Description</strong>
                                <p style={{ margin: 0, color: '#555', lineHeight: 1.7, fontSize: '0.95rem' }}>
                                    {selectedAssignment.description || 'No description provided.'}
                                </p>
                            </div>

                            {/* Info Grid */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: '1rem',
                                marginBottom: '1.5rem'
                            }}>
                                <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '0.8rem' }}>
                                    <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.2rem' }}>📅 Assigned Date</div>
                                    <div style={{ fontWeight: 600, color: '#333', fontSize: '0.9rem' }}>{formatDate(selectedAssignment.assignedDate)}</div>
                                </div>
                                <div style={{ background: selectedAssignment.isOverdue ? '#fef2f2' : '#f0fdf4', borderRadius: '8px', padding: '0.8rem' }}>
                                    <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.2rem' }}>⏰ Due Date</div>
                                    <div style={{ fontWeight: 600, color: selectedAssignment.isOverdue ? '#dc2626' : '#333', fontSize: '0.9rem' }}>{formatDate(selectedAssignment.dueDate)}</div>
                                </div>
                                <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '0.8rem' }}>
                                    <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.2rem' }}>📊 Total Marks</div>
                                    <div style={{ fontWeight: 600, color: '#238548', fontSize: '1.1rem' }}>{selectedAssignment.totalMarks}</div>
                                </div>
                                <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '0.8rem' }}>
                                    <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.2rem' }}>👨‍🏫 Teacher</div>
                                    <div style={{ fontWeight: 600, color: '#333', fontSize: '0.9rem' }}>{selectedAssignment.teacherName || 'N/A'}</div>
                                </div>
                                {selectedAssignment.semester && (
                                    <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '0.8rem' }}>
                                        <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.2rem' }}>📖 Semester</div>
                                        <div style={{ fontWeight: 600, color: '#333', fontSize: '0.9rem' }}>{selectedAssignment.semester}</div>
                                    </div>
                                )}
                                {selectedAssignment.marksObtained != null && (
                                    <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '0.8rem' }}>
                                        <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.2rem' }}>🏆 Marks Obtained</div>
                                        <div style={{ fontWeight: 700, color: '#238548', fontSize: '1.1rem' }}>{selectedAssignment.marksObtained}/{selectedAssignment.totalMarks}</div>
                                    </div>
                                )}
                            </div>

                            {/* Submission Info */}
                            {selectedAssignment.isSubmitted && (
                                <div style={{
                                    background: '#e8f5e9',
                                    border: '1px solid #a5d6a7',
                                    borderRadius: '8px',
                                    padding: '1rem',
                                    marginBottom: '1rem'
                                }}>
                                    <strong style={{ color: '#2e7d32', fontSize: '0.9rem' }}>✅ Submitted on:</strong>
                                    <span style={{ marginLeft: '0.5rem', color: '#555' }}>{formatDateTime(selectedAssignment.submittedAt || selectedAssignment.submittedDate)}</span>
                                </div>
                            )}

                            {/* Teacher Feedback */}
                            {selectedAssignment.feedback && (
                                <div style={{
                                    background: '#f0f8f4',
                                    border: '1px solid #97d6ac',
                                    borderRadius: '8px',
                                    padding: '1rem',
                                    marginBottom: '1rem'
                                }}>
                                    <strong style={{ color: '#238548', display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>💬 Teacher's Feedback:</strong>
                                    <p style={{ margin: 0, color: '#555', lineHeight: 1.6 }}>{selectedAssignment.feedback}</p>
                                </div>
                            )}

                            {/* Attachments / Download */}
                            {selectedAssignment.attachments && (() => {
                                // Parse attachments - could be JSON string or array
                                let files = [];
                                try {
                                    files = typeof selectedAssignment.attachments === 'string'
                                        ? JSON.parse(selectedAssignment.attachments)
                                        : selectedAssignment.attachments;
                                } catch { files = []; }
                                if (!Array.isArray(files) || files.length === 0) return null;

                                return (
                                    <div style={{ marginBottom: '1rem' }}>
                                        <strong style={{ display: 'block', marginBottom: '0.75rem', color: '#333', fontSize: '0.95rem' }}>📎 Attachments:</strong>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            {files.map((filePath, i) => {
                                                // filePath is either a string "/uploads/assignments/file.pdf" or an object
                                                const path = typeof filePath === 'string' ? filePath : (filePath.filePath || filePath.file_path || filePath.path || '');
                                                const fileName = path.split('/').pop() || `File ${i + 1}`;
                                                // Remove timestamp prefix from filename (e.g. "1772003688769-abhijit.pdf" → "abhijit.pdf")
                                                const displayName = fileName.replace(/^\d+-/, '');
                                                const downloadUrl = `${API_LINK}${path.startsWith('/') ? '' : '/'}${path}`;

                                                return (
                                                    <a
                                                        key={i}
                                                        href={downloadUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        download={displayName}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '0.75rem',
                                                            background: '#f0f7ff',
                                                            border: '1px solid #bbdefb',
                                                            padding: '0.75rem 1rem',
                                                            borderRadius: '8px',
                                                            textDecoration: 'none',
                                                            color: '#1565c0',
                                                            fontWeight: 500,
                                                            fontSize: '0.9rem',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.background = '#e3f2fd';
                                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.background = '#f0f7ff';
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                        }}
                                                    >
                                                        <span style={{
                                                            background: '#1565c0',
                                                            color: '#fff',
                                                            padding: '0.3rem 0.6rem',
                                                            borderRadius: '6px',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 700,
                                                            letterSpacing: '0.5px'
                                                        }}>
                                                            ⬇ Download
                                                        </span>
                                                        <span style={{ flex: 1 }}>{displayName}</span>
                                                        <span style={{ fontSize: '0.75rem', color: '#888' }}>
                                                            {displayName.split('.').pop().toUpperCase()}
                                                        </span>
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Close Button */}
                            <button
                                onClick={() => { setShowViewModal(false); setSelectedAssignment(null); }}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    backgroundColor: '#238548',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    marginTop: '0.5rem'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Submit Assignment Modal */}
            {showSubmitModal && selectedAssignment && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000,
                    padding: '1rem'
                }}>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: '12px',
                        maxWidth: '600px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                    }}>
                        {/* Modal Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                            color: '#ffffff',
                            padding: '1.5rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h3 style={{ margin: 0 }}>Submit Assignment</h3>
                            <button
                                onClick={() => {
                                    setShowSubmitModal(false);
                                    setSubmissionText('');
                                    setSelectedFiles([]);
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#ffffff',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    padding: '0',
                                    width: '30px',
                                    height: '30px'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: '1.5rem' }}>
                            <div style={{
                                background: '#f0f8f4',
                                border: '1px solid #97d6ac',
                                borderRadius: '8px',
                                padding: '1rem',
                                marginBottom: '1.5rem'
                            }}>
                                <h4 style={{ margin: '0 0 0.5rem 0', color: '#238548' }}>
                                    {selectedAssignment.title}
                                </h4>
                                <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                                    Due: {formatDate(selectedAssignment.dueDate)} • Total Marks: {selectedAssignment.totalMarks}
                                </p>
                            </div>

                            {/* Text Submission */}
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    color: '#333',
                                    fontWeight: '500'
                                }}>
                                    Written Submission (Optional)
                                </label>
                                <textarea
                                    value={submissionText}
                                    onChange={(e) => setSubmissionText(e.target.value)}
                                    placeholder="Enter your submission text here..."
                                    style={{
                                        width: '100%',
                                        minHeight: '150px',
                                        padding: '0.75rem',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '0.95rem',
                                        fontFamily: 'inherit',
                                        resize: 'vertical',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            {/* File Upload */}
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    color: '#333',
                                    fontWeight: '500'
                                }}>
                                    Upload Files (Optional - Max 3 files, 10MB each)
                                </label>
                                <input
                                    type="file"
                                    multiple
                                    accept=".pdf,.doc,.docx,image/*"
                                    onChange={handleFileSelect}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '2px dashed #ddd',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        boxSizing: 'border-box'
                                    }}
                                />
                                {selectedFiles.length > 0 && (
                                    <div style={{ marginTop: '0.5rem' }}>
                                        <strong style={{ fontSize: '0.9rem', color: '#666' }}>
                                            Selected files:
                                        </strong>
                                        <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
                                            {selectedFiles.map((file, index) => (
                                                <li key={index} style={{ fontSize: '0.85rem', color: '#666' }}>
                                                    {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>

                            {/* Submit Button */}
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <button
                                    onClick={() => {
                                        setShowSubmitModal(false);
                                        setSubmissionText('');
                                        setSelectedFiles([]);
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: '#f5f5f5',
                                        color: '#333',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontSize: '1rem',
                                        fontWeight: '500'
                                    }}
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmitAssignment}
                                    disabled={submitting || (!submissionText && selectedFiles.length === 0)}
                                    style={{
                                        flex: 1,
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: submitting ? '#ccc' : '#238548',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: submitting ? 'not-allowed' : 'pointer',
                                        fontSize: '1rem',
                                        fontWeight: '500'
                                    }}
                                >
                                    {submitting ? 'Submitting...' : '📤 Submit'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default StudentAssignment;
